import { Badge } from '@plato/ui-legacy/components/ui/badge';
import Text from '@plato/ui-legacy/components/ui/text';
import { MemoDataTable } from '@plato/ui/components/data-table';
import { cn } from '@plato/ui/util/classes';
import * as Accordion from '@radix-ui/react-accordion';
import { ChevronDownIcon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../utils/api.ts';
import { useTaskListSorting } from '../../hooks/use-task-list-sorting.ts';
import { useTaskOverviewColumns } from '../../hooks/use-task-overview-columns.tsx';
import { useTaskQueryBuilder } from '../../hooks/use-task-query-builder.ts';
import type { TaskViewGroup } from '../../types/task-group.ts';

export type TaskOverviewGroupProps = {
    group: TaskViewGroup;
};

export function TaskOverviewGroup({ group }: TaskOverviewGroupProps) {
    const { t } = useTranslation();
    const columns = useTaskOverviewColumns(group);
    const filters = useTaskQueryBuilder(group);

    const getTaskGroupLabel = (taskGroup: TaskViewGroup): string => {
        switch (taskGroup) {
            case 'completed':
                return t('tasks__group_completed', { ns: 'tasks' });
            case 'today':
                return t('tasks__group_today', { ns: 'tasks' });
            case 'upcoming':
                return t('tasks__group_upcoming', { ns: 'tasks' });
        }
    };
    const [sorting, onSortingChange] = useTaskListSorting();

    const { data, isLoading } = api.platoUser.query.tasks.getManyTasks.getListWithFilters.useQuery(filters, {
        enabled: true,
    });

    const count = data?.length;

    if (!isLoading && count === 0) {
        return null;
    }

    return (
        <Accordion.Item value={group}>
            <Accordion.Header className="flex">
                <Accordion.Trigger
                    className={cn(
                        'flex w-full flex-col py-2 text-left transition-all',
                        '[&[data-state=open]>div>#close-text]:hidden [&[data-state=open]>div>#open-text]:flex',
                        '[&[data-state=open]>div>svg]:rotate-180',
                        'border-b border-neutral-200',
                    )}
                >
                    <div className="flex items-center gap-2">
                        <ChevronDownIcon className="h-4 w-4" />
                        <Text size="sm" weight="medium" color="neutral-600">
                            {getTaskGroupLabel(group)}
                        </Text>
                        {count !== undefined && (
                            <Badge
                                variant={group === 'today' ? 'warning' : 'neutral'}
                                className="rounded-full px-2 py-0.5 text-xs font-bold"
                            >
                                {count}
                            </Badge>
                        )}
                    </div>
                </Accordion.Trigger>
            </Accordion.Header>
            <Accordion.Content className="py-2">
                <MemoDataTable
                    emptyLabel={t('empty_table__no_results')}
                    columns={columns}
                    data={data}
                    isLoading={isLoading}
                    manualSorting
                    sorting={sorting}
                    onSortingChange={onSortingChange}
                />
            </Accordion.Content>
        </Accordion.Item>
    );
}
